/**
 * @fileoverview useNewOutputBoundary — "new since you last viewed" hook.
 *
 * Purpose:
 * - When the user opens an agent's detail view, record it (advancing the stored
 *   cursor) and return the PREVIOUS view timestamp, so the entity table can
 *   badge rows created after it.
 *
 * Timing model — a "New" badge should:
 * - stay put while the user is looking at the table (not flicker off mid-view),
 * - clear the next time the user comes back to that table (they've seen it).
 *
 * The view is a mounted panel, so the cursor is advanced once per mount and
 * each genuine revisit (navigating away and back remounts the panel) re-marks,
 * moving the boundary past rows already seen. The effect's `markViewed` dep is
 * memoized by Convex, so it never re-runs on a plain re-render — only on a real
 * mount. The one non-genuine remount to guard against is React Strict Mode's
 * synchronous mount/unmount/mount in dev; in-flight coalescing handles it, since
 * both mounts share the single pending `markAgentViewed` network round-trip and
 * therefore advance the cursor only once.
 */

import { useEffect, useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

/** In-flight `markAgentViewed` calls, so concurrent mounts share one write. */
const inFlightMarks = new Map<string, Promise<number | null>>();

/** Tooltip shown on a newly-created output's "New" badge. */
export const NEW_OUTPUT_TITLE = "New since you last viewed";

/**
 * Solid "new / completed results" accent (primary green), shared by the `New`
 * output badge and the sidebar's completed-run pill so both signals read as the
 * same thing. Pairs with white text. Single source of truth so the two — and
 * the lighter "running" pill derived from the same hue — stay in sync.
 */
export const NEW_OUTPUT_ACCENT_CLASS = "bg-emerald-600 dark:bg-emerald-500";

/**
 * Whether an entity row was created after the captured view boundary.
 *
 * @param creationTime - The row's `_creationTime` (epoch ms).
 * @param boundary - The boundary from `useNewOutputBoundary`, or `null`.
 * @returns `true` when the row is new relative to the boundary.
 */
export function isNewOutput(
  creationTime: number,
  boundary: number | null,
): boolean {
  return boundary !== null && creationTime > boundary;
}

/**
 * Marks the given agent's outputs as viewed and returns the boundary timestamp
 * to compare each row's `_creationTime` against.
 *
 * @param jobId - The job whose agent view is being opened (may be undefined
 *   while the route params resolve).
 * @param agentKey - Stable agent identifier (e.g. "sum_asset_list").
 * @returns The view boundary, or `null` until it resolves / when unauthenticated.
 *   Rows created after it are "new".
 */
export function useNewOutputBoundary(
  jobId: Id<"jobs"> | undefined,
  agentKey: string,
): number | null {
  const markViewed = useConvexMutation(
    api.notifications.agentViewCursors.markAgentViewed,
  );
  const key = jobId ? `${jobId}:${agentKey}` : null;
  const [boundary, setBoundary] = useState<number | null>(null);

  useEffect(() => {
    if (!jobId || !key) return;

    let cancelled = false;
    // Coalesce the Strict Mode double-mount onto a single write so the cursor
    // advances once per real mount. A genuine revisit is a fresh mount and
    // re-marks, moving the boundary past rows the user has already seen.
    const pending = inFlightMarks.get(key) ?? markViewed({ jobId, agentKey });
    inFlightMarks.set(key, pending);
    void pending
      .then((previous) => {
        inFlightMarks.delete(key);
        if (!cancelled) setBoundary(previous);
      })
      .catch(() => {
        // Non-critical: a failed cursor write just means no badge.
        inFlightMarks.delete(key);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, key, agentKey, markViewed]);

  return boundary;
}
